import React, { useState } from 'react';
import { ChecklistItem } from '@/types/lead';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Plus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

interface TaskChecklistProps {
  items: ChecklistItem[];
  onChange: (items: ChecklistItem[]) => void;
}

const TaskChecklist: React.FC<TaskChecklistProps> = ({ items, onChange }) => {
  const [newItemText, setNewItemText] = useState('');

  const completedCount = items.filter(i => i.completed).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleToggle = (id: string) => {
    onChange(items.map(item => item.id === id ? { ...item, completed: !item.completed } : item));
  };

  const handleDelete = (id: string) => {
    onChange(items.filter(item => item.id !== id));
  };

  const handleAdd = () => {
    const text = newItemText.trim();
    if (!text) return;
    onChange([...items, { id: uuidv4(), text, completed: false }]);
    setNewItemText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Checklist</h4>
        {totalCount > 0 && (
          <span className="text-xs text-muted-foreground">{completedCount}/{totalCount} completed</span>
        )}
      </div>

      {totalCount > 0 && (
        <Progress value={progressPercent} className="h-1.5" />
      )}

      <div className="space-y-1.5">
        {items.map(item => (
          <div key={item.id} className="flex items-center gap-2 group">
            <Checkbox
              checked={item.completed}
              onCheckedChange={() => handleToggle(item.id)}
            />
            <span className={`text-sm flex-1 ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
              {item.text}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => handleDelete(item.id)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Input
          placeholder="Add checklist item..."
          value={newItemText}
          onChange={e => setNewItemText(e.target.value)}
          onKeyDown={handleKeyDown}
          className="text-sm h-8"
        />
        <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={handleAdd} disabled={!newItemText.trim()}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default TaskChecklist;
